import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingCart, Star, Eye } from 'lucide-react';
import { Product, formatPrice, ROUTE_PATHS, calculateDiscount } from '@/lib/index';
import { useCart } from '@/hooks/useCart';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { springPresets } from '@/lib/motion';
import { useToast } from '@/hooks/use-toast';

interface ProductCardProps {
  product: Product;
  featured?: boolean;
}

export function ProductCard({ product, featured = false }: ProductCardProps) {
  const addToCart = useCart((state) => state.addToCart);
  const { toast } = useToast();

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product, 1);
    toast({
      title: 'تمت الإضافة للسلة',
      description: `${product.name} تمت إضافته لسلة التسوق`,
    });
  };

  const isLowStock = product.stock > 0 && product.stock <= 10;
  const isOutOfStock = product.stock === 0;
  const discountPercentage = calculateDiscount(product.originalPrice, product.price);
  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-3.5 h-3.5 ${
              star <= rating
                ? 'fill-primary text-primary'
                : 'fill-muted text-muted'
            }`}
          />
        ))}
      </div>
    );
  };

  return (
    <Link to={ROUTE_PATHS.PRODUCT_DETAIL.replace(':id', product.id)}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={springPresets.gentle}
        whileHover={{ y: -4 }}
      >
        <Card className="group relative overflow-hidden border-border hover:shadow-lg transition-all duration-300" dir="rtl">
          {/* Discount Badge */}
          {discountPercentage > 0 && (
            <Badge
              className="absolute top-3 right-3 z-10 bg-red-500 text-white text-base font-bold px-3 py-1"
            >
              -{discountPercentage}%
            </Badge>
          )}

          {/* Featured Badge */}
          {(featured || product.featured) && !discountPercentage && (
            <Badge
              className="absolute top-3 right-3 z-10 bg-accent text-accent-foreground"
            >
              <Star className="w-3 h-3 ml-1 fill-current" />
              مميز
            </Badge>
          )}

          {/* Stock Status Badge */}
          {isOutOfStock && (
            <Badge
              variant="destructive"
              className="absolute top-3 left-3 z-10"
            >
              نفذت الكمية
            </Badge>
          )}
          {isLowStock && !isOutOfStock && (
            <Badge
              className="absolute top-3 left-3 z-10 bg-amber-500 text-white"
            >
              {product.stock} قطعة فقط
            </Badge>
          )}

          {/* Product Image */}
          <div className="relative aspect-square overflow-hidden bg-muted">
            <img
              src={product.image}
              alt={product.name}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            />
            {isOutOfStock && (
              <div className="absolute inset-0 bg-background/80 flex items-center justify-center">
                <span className="text-lg font-semibold text-muted-foreground">
                  غير متوفر
                </span>
              </div>
            )}
          </div>

          <CardContent className="p-4">
            {/* Brand Badge */}
            <Badge variant="outline" className="mb-2">
              {product.brand}
            </Badge>

            {/* Product Name */}
            <h3 className="font-semibold text-lg mb-2 line-clamp-2 group-hover:text-primary transition-colors">
              {product.name}
            </h3>

            {/* Rating and Views */}
            <div className="flex items-center justify-between mb-3 gap-2">
              <div className="flex items-center gap-1 flex-shrink-0">
                {renderStars(product.rating)}
                <span className="text-xs text-muted-foreground mr-1">
                  ({product.reviewCount})
                </span>
              </div>
              <div className="flex items-center gap-1 text-xs text-muted-foreground flex-shrink-0">
                <Eye className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="whitespace-nowrap">{product.views.toLocaleString('ar-EG')}</span>
              </div>
            </div>

            {/* Price */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-2xl font-bold text-primary">
                {formatPrice(product.price)}
              </span>
              {product.originalPrice && product.originalPrice > product.price && (
                <span className="text-lg text-muted-foreground line-through">
                  {formatPrice(product.originalPrice)}
                </span>
              )}
            </div>
          </CardContent>

          <CardFooter className="p-4 pt-0">
            <Button
              onClick={handleAddToCart}
              disabled={isOutOfStock}
              className="w-full"
              size="lg"
            >
              <ShoppingCart className="w-4 h-4 ml-2" />
              {isOutOfStock ? 'نفذت الكمية' : 'أضف للسلة'}
            </Button>
          </CardFooter>

          {/* Rim Light Effect */}
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-muted-foreground/20 to-transparent" />
        </Card>
      </motion.div>
    </Link>
  );
}